import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { GoogleLogin } from '@react-oauth/google';
import { login, googleLogin, requestPasswordReset } from '../services/api';
import { useTranslation } from 'react-i18next';

const LoginModal = ({ show, onHide, onSwitchToRegister, onLoginSuccess }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [view, setView] = useState('login'); // 'login' | 'forgot'
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotSent, setForgotSent] = useState(false);
    const [forgotLoading, setForgotLoading] = useState(false);
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const isEn = i18n.language === 'en';

    const resetState = () => {
        setView('login'); setForgotEmail(''); setForgotSent(false); setError(null);
    };

    const handleForgot = async (e) => {
        e.preventDefault();
        setForgotLoading(true);
        try {
            await requestPasswordReset(forgotEmail);
            setForgotSent(true);
        } catch {
            setForgotSent(true); // don't reveal if email exists
        } finally {
            setForgotLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        try {
            await login(username, password);
            onHide();
            if (onLoginSuccess) onLoginSuccess();
            navigate('/');
        } catch (error) {
            setError(error.response?.data?.error || (isEn ? "Invalid credentials. Please try again." : "Credenziali non valide. Riprova."));
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await googleLogin(credentialResponse.credential, 'login');
            localStorage.setItem('token', data.token);
            localStorage.setItem('login_time', Date.now().toString());
            localStorage.setItem('user', JSON.stringify({
                id: data.user_id,
                username: data.username,
                email: data.email,
            }));
            onHide();
            if (onLoginSuccess) onLoginSuccess();
            navigate('/');
        } catch (err) {
            const msg = err?.response?.data?.error;
            if (msg) {
                setError(isEn ? 'You don\'t have an account yet. Click "Sign up" to create one.' : 'Non hai ancora un account. Clicca "Registrati" per crearne uno.');
            } else {
                setError(isEn ? "Google sign-in failed. Please try again." : "Accesso Google fallito. Riprova.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={() => { resetState(); onHide(); }} centered>
            <Modal.Header closeButton style={{ border: 'none', paddingBottom: 0 }}>
                <Modal.Title style={{ fontWeight: 800, fontSize: '1.2rem' }}>
                    {view === 'forgot' ? (isEn ? 'Forgot password' : 'Password dimenticata') : (isEn ? 'Log in' : 'Accedi')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '1.25rem 2rem 1.75rem' }}>

            {/* ── Forgot password view ── */}
            {view === 'forgot' && (
                <>
                    {forgotSent ? (
                        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📬</div>
                            <p style={{ fontWeight: 700, marginBottom: '0.5rem' }}>{isEn ? 'Email sent!' : 'Email inviata!'}</p>
                            <p style={{ fontSize: '0.88rem', color: '#64748B' }}>
                                {isEn ? 'If the address is registered you will receive a link to reset your password.' : 'Se l\'indirizzo è registrato riceverai un link per reimpostare la password.'}
                            </p>
                            <button type="button" onClick={resetState}
                                style={{ marginTop: '1rem', background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 700, cursor: 'pointer' }}>
                                {isEn ? '← Back to login' : '← Torna al login'}
                            </button>
                        </div>
                    ) : (
                        <Form onSubmit={handleForgot}>
                            <p style={{ fontSize: '0.88rem', color: '#64748B', marginBottom: '1rem' }}>
                                {isEn ? 'Enter your account email and we\'ll send you a link to reset your password.' : 'Inserisci l\'email del tuo account e ti invieremo un link per reimpostare la password.'}
                            </p>
                            <Form.Group className="mb-3">
                                <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>Email</Form.Label>
                                <Form.Control type="email" placeholder="email@example.com" value={forgotEmail}
                                    onChange={e => setForgotEmail(e.target.value)} required
                                    style={{ borderRadius: 12, height: 46 }} />
                            </Form.Group>
                            <Button type="submit" disabled={forgotLoading}
                                style={{ width: '100%', background: 'linear-gradient(135deg,var(--primary),var(--secondary))', border: 'none', borderRadius: '9999px', fontWeight: 700, height: 46 }}>
                                {forgotLoading ? <Spinner animation="border" size="sm" /> : (isEn ? 'Send reset link' : 'Invia link di reset')}
                            </Button>
                            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                                <button type="button" onClick={resetState}
                                    style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' }}>
                                    {isEn ? '← Back to login' : '← Torna al login'}
                                </button>
                            </div>
                        </Form>
                    )}
                </>
            )}

            {/* ── Login view ── */}
            {view === 'login' && (<>
                {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

                {/* Google button */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.25rem' }}>
                    <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => setError(isEn ? "Google sign-in failed." : "Accesso Google fallito.")}
                        text="signin_with"
                        shape="pill"
                        locale={isEn ? 'en' : 'it'}
                        useOneTap={false}
                    />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
                    <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                    <span style={{ fontSize: '0.78rem', color: '#9ca3af', fontWeight: 600 }}>{isEn ? 'or' : 'oppure'}</span>
                    <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                </div>

                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>Username</Form.Label>
                        <Form.Control type="text" placeholder={isEn ? 'Enter username' : 'Inserisci username'} value={username}
                            onChange={(e) => setUsername(e.target.value)} required
                            style={{ borderRadius: 12, height: 46 }} />
                    </Form.Group>
                    <Form.Group className="mb-1">
                        <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>Password</Form.Label>
                        <Form.Control type="password" placeholder="••••••••" value={password}
                            onChange={(e) => setPassword(e.target.value)} required
                            style={{ borderRadius: 12, height: 46 }} />
                    </Form.Group>
                    <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
                        <button type="button" onClick={() => { setError(null); setView('forgot'); }}
                            style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '0.8rem', cursor: 'pointer', padding: 0 }}>
                            {isEn ? 'Forgot password?' : 'Password dimenticata?'}
                        </button>
                    </div>
                    <Button type="submit" disabled={isLoading}
                        style={{ width: '100%', background: 'linear-gradient(135deg,var(--primary),var(--secondary))', border: 'none', borderRadius: '9999px', fontWeight: 700, height: 46 }}>
                        {isLoading ? <Spinner animation="border" size="sm" /> : (isEn ? 'Log in' : 'Accedi')}
                    </Button>
                </Form>

                <div style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.85rem', color: '#64748B' }}>
                    {isEn ? "Don't have an account yet?" : 'Non hai ancora un account?'}{' '}
                    <button type="button" style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 700, cursor: 'pointer', padding: 0 }}
                        onClick={() => { onHide(); onSwitchToRegister?.(); }}>
                        {isEn ? 'Sign up' : 'Registrati'}
                    </button>
                </div>
            </>)}
            </Modal.Body>
        </Modal>
    );
};

export default LoginModal;
